import { isRemovalOrDoorToDoor } from "../../utils/Validation";
import { createSlice } from "@reduxjs/toolkit";
import { leadEstimateInitial, leadQuoteInitial } from "./leadUtils";

export const initialLeadLocalState = {
  referrerId: "",
  clientReferrerId: "",
  customer: {
    name: "",
    country_code: "",
    phone: "",
    alternate_country_code: "",
    alternate_phone: "",
    email: "",
    // professional field
    account_name: "",
    prefix: "",
  },
  collectionAddress: [
    {
      building_number: "",
      street: "",
      town: "",
      county: "",
      postcode: "",
      country: "",
      purchaseStatus: "",
      houseSize: "",
      distance: null,
      volume: 0,
      volumeUnit: "m3",
    },
  ],
  deliveryAddress: [
    {
      building_number: "",
      street: "",
      town: "",
      county: "",
      postcode: "",
      country: "",
      purchaseStatus: "",
      houseSize: "",
      distance: null,
      volume: 0,
      volumeUnit: "m3",
    },
  ],
  moveType: "",
  followUpDate: "",
  followUpEndDate: "",
  movingOnDate: "",
  movingOnEndDate: "",
  packingOnDate: "",
  returningDistance: 0,
  customerNotes: "",
  batch: "",
  inceptBatch: "",
  leadDate: "",
  // professional plan
  advert_id: "",
  promo_code: "",
  promo_source: "",
  sales_person_id: "",
  sales_person_name: "",
  lead_type_id: "",
};

const saveLeadDetailsToSession = (leadDetails: any) => {
  sessionStorage.setItem("leadDetails", JSON.stringify(leadDetails));
};

const loadLeadDetailsFromSession = () => {
  const leadDetails = sessionStorage.getItem("leadDetails");
  return leadDetails ? JSON.parse(leadDetails) : null;
};

const initialState = {
  newLeadLocal: { ...initialLeadLocalState },
  leads: [],
  logs: [],
  referrers: [],
  leadPagination: {},
  leadLogPagination: {},
  leadDetails: {},
  isLoading: false,
  error: null,
  isAddLeadSuccess: false,
  isUpdateLeadSuccess: false,
  isLeadPostCodeSuccess: false,
  newLeadEstimate: { ...leadEstimateInitial },
  distanceDeliveryValue: null,
  distanceCollectionValue: null,
  distanceReturnValue: null,
  selectedLeadEstimate: null,
  selectedLeadQuote: null as any,
  isFileDownloaded: false,
  isAddLeadLogSuccess: false,
  isLeadListSuccess: false,
  isSingleEstimateResponseSuccess: false,
  leadAddressDeletedResponse: false,
  isSingleLeadQuoteDeletedSuccess: false,
  updateLeadQuoteResponse: false,
  updateLeadEstimateResponse: false,
  accountNamesList: [],
  leadCustomers: [],
  previousQuotes: [],
};

const leadSlice = createSlice({
  name: "lead",
  initialState,
  reducers: {
    // Add new empty collection address
    addCollectionAddress: (state) => {
      state.newLeadLocal.collectionAddress.push({
        building_number: "",
        street: "",
        town: "",
        county: "",
        postcode: "",
        country: "",
        purchaseStatus: "",
        houseSize: "",
        distance: null,
        volume: 0,
        volumeUnit: "m3",
      });
    },

    // Update specific collection address
    updateCollectionAddress: (state, { payload }) => {
      const { index, address } = payload;
      if (state.newLeadLocal.collectionAddress[index]) {
        state.newLeadLocal.collectionAddress[index] = {
          ...state.newLeadLocal.collectionAddress[index],
          ...address,
        };
      }
    },

    // Remove specific collection address
    removeCollectionAddress: (state, { payload }) => {
      const index = payload;
      if (index > -1 && index < state.newLeadLocal.collectionAddress.length) {
        state.newLeadLocal.collectionAddress.splice(index, 1);
      }
    },

    // Add new empty delivery address
    addDeliveryAddress: (state) => {
      state.newLeadLocal.deliveryAddress.push({
        building_number: "",
        street: "",
        town: "",
        county: "",
        postcode: "",
        country: "",
        purchaseStatus: "",
        houseSize: "",
        distance: null,
        volume: 0,
        volumeUnit: "m3",
      });
    },

    // Update specific delivery address
    updateDeliveryAddress: (state, { payload }) => {
      const { index, address } = payload;
      if (state.newLeadLocal.deliveryAddress[index]) {
        state.newLeadLocal.deliveryAddress[index] = {
          ...state.newLeadLocal.deliveryAddress[index],
          ...address,
        };
      }
    },

    // Remove specific delivery address
    removeDeliveryAddress: (state, { payload }) => {
      const index = payload;
      if (index > -1 && index < state.newLeadLocal.deliveryAddress.length) {
        state.newLeadLocal.deliveryAddress.splice(index, 1);
      }
    },

    deleteLeadAddressAction: (state, { payload }) => {},
    setLeadAddressDeletedAction: (state, { payload }) => {
      state.leadAddressDeletedResponse = payload;
    },

    getAllLeadListAction: (state, { payload }) => {},
    setAllLeadListAction: (state, { payload }) => {
      const { pagination = { currentPage: 1 }, list = [] } = payload;
      state.leads = list;
      state.leadPagination = pagination;
    },

    setLeadListAction: (state, { payload }) => {
      state.isLeadListSuccess = payload;
    },

    setNewLeadLocal: (state, action) => {
      state.newLeadLocal = {
        ...state.newLeadLocal,
        ...action.payload,
      };
    },

    resetNewLeadLocal: (state) => {
      state.newLeadLocal = { ...initialLeadLocalState };
    },

    addNewLeadAction: (state, { payload }) => {},
    setAddNewLeadAction: (state, { payload }) => {
      state.isAddLeadSuccess = payload;
    },

    updateLeadAction: (state, action) => {},
    setUpdateLeadAction: (state, { payload }) => {
      state.isUpdateLeadSuccess = payload;
    },

    getSingleLeadAction: (state, { payload }) => {},
    setSingleLeadAction: (state, { payload }) => {
      state.leadDetails = payload;
      // Check if the role is "TENANT" before saving to session
      // const storedUser = localStorage.getItem("user");
      // if (storedUser) {
      //   const userProfile = JSON.parse(storedUser);
      //   if (
      //     userProfile?.user?.roles?.includes("TENANT") ||
      //     userProfile?.user?.roles?.includes("Admin") ||
      //     userProfile?.user?.roles?.includes("Owner")
      //   ) {
      saveLeadDetailsToSession(payload);
      //   }
      // }
    },

    clearLeadDetails: (state) => {
      state.leadDetails = {};
      sessionStorage.removeItem("leadDetails"); // Clear from sessionStorage
    },

    getAllReferrerListAction: (state, action) => {},
    setAllReferrerListAction: (state, { payload }) => {
      state.referrers = payload;
    },

    addLeadLogAction: (state, { payload }) => {},
    setAddLeadLogAction: (state, { payload }) => {
      state.isAddLeadLogSuccess = payload;
    },

    getAllLeadLogListAction: (state, { payload }) => {},
    setAllLeadLogListAction: (state, { payload }) => {
      const { pagination = { currentPage: 1 }, list = [] } = payload;
      state.logs = list;
      state.leadLogPagination = pagination;
    },

    addLeadEstimateAction: (state, { payload }) => {},
    setUpdateLeadEstimateResponse: (state, { payload }) => {
      state.updateLeadEstimateResponse = payload;
    },

    addLeadQuoteAction: (state, { payload }) => {},
    setUpdateLeadQuoteResponse: (state, { payload }) => {
      state.updateLeadQuoteResponse = payload;
    },

    getLeadEstimateAction: (state, { payload }) => {},
    setLeadEstimateAction: (state, { payload }) => {
      // state.selectedLeadEstimate = payload;
      if (payload) {
        const cloneSelectedLeadEstimate = payload;

        const newObj: any = cloneSelectedLeadEstimate;
        // Remove `data` & messagee if it's an empty object
        if (newObj?.data && Object.keys(newObj?.data).length === 0) {
          delete newObj?.data;
          delete newObj?.message;
        }

        let servicesArr: any = [];
        const serviceCopy = newObj?.services ? [...newObj?.services] : [];

        // Create a map for existing services (ignoring case sensitivity)
        const normalize = (str: any) =>
          str ? str.toLowerCase().replace(/\s+/g, "") : ""; // Remove spaces and convert to lowercase

        const serviceMap = new Map(
          serviceCopy.map((service, index) => {
            const serviceKey =
              normalize(
                service.type || service.service_name || service.typeName,
              ) || `service_${index}`;

            return [serviceKey, service];
          }),
        );
        leadEstimateInitial.services.forEach((ele, idx) => {
          const matchedService = serviceMap.get(normalize(ele.type));
          if (matchedService) {
            // Apply `isAccepted` check only for `insurance` and `unpack`
            const shouldInclude = [
              "insurance",
              "unpack",
              "intostore",
              "outofstore",
              "clearance",
              "dismantle",
              "fragilepack",
              "vehicleHire",
              "exportPack",
              "storage",
            ].includes(normalize(ele.type))
              ? matchedService.isAccepted
              : true;

            if (matchedService && shouldInclude) {
              servicesArr.push({
                ...ele,
                ...matchedService,
                typeName: ele.typeName || matchedService.service_name, // Ensure `typeName` is correctly mapped
                serviceId: matchedService.serviceId || matchedService.id, // Map ID field
                isAccepted:
                  matchedService.isAccepted ??
                  matchedService.is_accepted ??
                  false, // Handle both cases
                orderIndex: idx + 1,
              });
            }
            serviceMap.delete(normalize(ele.type)); // Remove from map to prevent duplicate usage
          } else if (
            ["dismantle", "fullpack", "removal"].includes(normalize(ele.type))
          ) {
            servicesArr.push({
              ...ele,
              orderIndex: idx + 1,
            });
          }
        });

        const acceptedServiceNames = [
          "insurance",
          "unpack",
          "intostore",
          "outofstore",
          "clearance",
          "dismantle",
          "fragilepack",
          "vehicleHire",
          "exportPack",
          "storage",
        ];

        // Convert remaining services from API into the expected format
        const additionalServices = Array.from(serviceMap.values())
          .filter((service) => {
            const name = normalize(service.service_name || service.typeName);

            if (acceptedServiceNames.includes(name)) {
              // For specific services, only include if accepted
              return (
                service?.serviceId || service?.service_id
                // service?.is_accepted === true || service?.isAccepted === true
              );
            }

            // For other services, include if they have a serviceId or match one of the general ones
            return (
              service?.serviceId ||
              ["dismantle", "fullpack", "removal"].includes(name)
            );
          })
          .map((service, idx) => ({
            typeName: service.type_name
              ? service.type_name.replace(/\b\w/g, (char: any) =>
                  char.toUpperCase(),
                )
              : service?.typeName,
            serviceId: service.serviceId, // Map ID field
            description:
              service.description ||
              `${
                service.type_name === "Removal"
                  ? "Removal Door To Door"
                  : service?.type_name + " Service"
              }`,
            price: service.price ?? null,
            type: service?.type || service?.service_name || "",
            driverQty: service.driverQty ?? null,
            porterQty: service.porterQty ?? null,
            packerQty: service.packerQty ?? null,
            // vehicles: service.vehicles ?? [], // Handle vehicles array if present
            vehicleQty: service.vehicleQty ?? null,
            vehicleTypeId: service.vehicleTypeId ?? "",
            wageCharge: service.wageCharge ?? null,
            fuelCharge: service.fuelCharge ?? null,
            orderIndex: servicesArr.length + idx + 1,
            isAccepted: service.isAccepted ?? service.is_accepted ?? false, // Handle both cases
          }));

        servicesArr = [...servicesArr, ...additionalServices];

        // Check if either 'Removal' or 'Removal, Door to Door' is present
        const hasRemovalOrDoorToDoor = servicesArr.some((service: any) =>
          isRemovalOrDoorToDoor(service.typeName),
        );

        newObj.services = servicesArr;

        let materialsArr: any = [];
        newObj?.materials?.map((ele: any) => {
          materialsArr.push({
            name: ele?.name,
            surveyedQty: ele?.surveyedQty || 0,
            chargeQty: ele?.chargeQty || 0,
            price: ele?.price || 0,
            total: ele?.total || 0,
            volume: ele?.volume || 0,
            cost: ele?.cost || 0,
          });
        });

        materialsArr = [
          ...materialsArr,
          ...leadEstimateInitial.materials.slice(materialsArr?.length, 9),
        ];
        newObj.materials = materialsArr;

        let ancillariesArr: any = newObj?.ancillaries
          ? [...newObj?.ancillaries]
          : [];
        ancillariesArr = [
          ...ancillariesArr,
          ...leadEstimateInitial.ancillaries.slice(ancillariesArr?.length, 3),
        ];
        newObj.ancillaries = ancillariesArr;

        // Ensure `generalInfo` remains an object
        if (
          !newObj.generalInfo ||
          typeof newObj.generalInfo !== "object" ||
          Array.isArray(newObj.generalInfo)
        ) {
          newObj.generalInfo = {};
        }
        newObj.generalInfo = {
          ...leadEstimateInitial.generalInfo,
          ...newObj.generalInfo,
        };

        state.selectedLeadEstimate = {
          ...leadEstimateInitial,
          ...newObj,
          hasRemovalOrDoorToDoor, // for validation use
        };
      } else if (payload === null) {
        state.selectedLeadEstimate = null;
      }
    },

    // new quote screen
    setLeadQuoteAction: (state, { payload }) => {
      console.log("payload-quote-490", payload);
      if (!payload) {
        state.selectedLeadQuote = null;
        return;
      }

      const normalizeServiceKey = (value: any) =>
        String(value || "")
          .toLowerCase()
          .replace(/\s+/g, "")
          .replace(/[,_-]+/g, "");

      const defaultServiceKeys = new Set<string>();
      (payload?.defaultServiceList || []).forEach((service: any) => {
        const typeKey = normalizeServiceKey(service?.type);
        const nameKey = normalizeServiceKey(service?.name);
        if (typeKey) defaultServiceKeys.add(typeKey);
        if (nameKey) defaultServiceKeys.add(nameKey);
      });

      /* ================= QUOTES NORMALIZATION ================= */

      const quotes = (payload.quotes || []).map((quote: any) => {
        /* ================= PROCESS MATERIALS FOR THIS QUOTE ================= */
        const baseMaterials = (quote.materials || []).map((ele: any) => ({
          name: ele?.name,
          surveyedQty: ele?.surveyedQty || 0,
          chargeQty: ele?.chargeQty || 0,
          price: ele?.price || 0,
          total: ele?.total || 0,
          volume: ele?.volume || 0,
          cost: ele?.cost || 0,
        }));

        const finalMaterials = [
          ...baseMaterials,
          ...leadQuoteInitial.materials.slice(baseMaterials.length, 9),
        ];

        /* ================= PROCESS ANCILLARIES FOR THIS QUOTE ================= */
        const baseAncillaries = quote.ancillaries || [];
        const finalAncillaries = [
          ...baseAncillaries,
          ...leadQuoteInitial.ancillaries.slice(baseAncillaries.length, 3),
        ];

        /* ================= PROCESS SUBCONTRACTORS FOR THIS QUOTE ================= */
        const baseSubcontractors = (quote.subcontractorCharges || []).map(
          (ele: any) => ({
            subContractorId: ele?.subcontractorId || ele?.subContractorId || "",
            chargeAmount: ele?.chargeAmount || 0,
            name: ele?.name || "",
          }),
        );

        const finalSubcontractors = [
          ...baseSubcontractors,
          ...leadQuoteInitial.subcontractorCharges.slice(
            baseSubcontractors.length,
            3,
          ),
        ];

        /* ================= PROCESS GENERAL INFO FOR THIS QUOTE ================= */
        const generalInfo =
          quote.generalInfo && typeof quote.generalInfo === "object"
            ? { ...leadQuoteInitial.generalInfo, ...quote.generalInfo }
            : { ...leadQuoteInitial.generalInfo };

        /* ================= PROCESS SERVICES FOR THIS QUOTE ================= */
        const servicesArr: any[] = (quote?.services || [])
          .map((service: any, idx: number) => {
            const serviceKey = normalizeServiceKey(
              service?.service_name ||
                service?.type ||
                service?.typeName ||
                service?.type_name,
            );
            const isRemovalService = serviceKey === "removal";

            return {
              serviceKey,
              typeName: String(
                service?.typeName ||
                  service?.type_name ||
                  service?.service_name ||
                  "",
              ).trim(),
              serviceId:
                service?.serviceId ||
                service?.service_id ||
                service?.id ||
                null,
              day:
                isRemovalService &&
                (service?.day === null || service?.day === undefined)
                  ? 1
                  : (service?.day ?? null),
              provisionalDate: service?.provisionalDate ?? null,
              type: service?.type || service?.service_name || "",
              description: service?.description || "",
              price: service?.price ?? null,
              driverQty: service?.driverQty ?? null,
              porterQty: service?.porterQty ?? null,
              packerQty: service?.packerQty ?? null,
              vehicles: service?.vehicles ?? [],
              wageCharge: service?.wageCharge ?? null,
              orderIndex: service?.orderIndex ?? idx + 1,
              isAccepted: service?.isAccepted ?? service?.is_accepted ?? false,
            };
          })
          .filter((service: any) => {
            const isDefaultService = defaultServiceKeys.has(
              service?.serviceKey,
            );
            const hasValidPrice =
              service?.price !== null &&
              service?.price !== undefined &&
              service?.price !== "";
            const isAccepted = service?.isAccepted === true;

            if (isDefaultService) {
              return hasValidPrice || isAccepted;
            }

            return hasValidPrice || isAccepted;
          })
          .map((service: any) => {
            const { serviceKey, ...rest } = service;
            return rest;
          })
          .sort((a: any, b: any) => {
            const aOrder =
              a?.orderIndex === null || a?.orderIndex === undefined
                ? Number.MAX_SAFE_INTEGER
                : Number(a.orderIndex);
            const bOrder =
              b?.orderIndex === null || b?.orderIndex === undefined
                ? Number.MAX_SAFE_INTEGER
                : Number(b.orderIndex);
            return aOrder - bOrder;
          });

        // Check if either 'Removal' or 'Removal, Door to Door' is present in this quote's services
        const hasRemovalOrDoorToDoor = servicesArr.some((service) =>
          isRemovalOrDoorToDoor(service?.type),
        );

        /* ================= RETURN COMPLETE QUOTE OBJECT ================= */
        return {
          quoteId: quote?.quoteId || null, // Include quoteId for updates
          services: servicesArr,
          hasRemovalOrDoorToDoor, // for validation use
          materials: finalMaterials,
          ancillaries: finalAncillaries,
          subcontractorCharges: finalSubcontractors,
          generalInfo: generalInfo,
          notes: quote?.notes || "",
          transportnote: quote?.transportnote || quote?.transportnote || "",
          quotenote: quote?.quotenote || quote?.quotenote || "",
          isaccepted: quote?.isaccepted || false,
          quoteTotal: quote?.hidequotetotal ? 0 : quote?.quoteTotal || 0,
          costTotal: quote?.costTotal || 0,
          vatIncluded: quote?.vatIncluded || false,
          materialPriceChargeable: quote?.materialPriceChargeable || false,
          requirements: quote?.requirements || null,
          paymentMethod: quote?.paymentMethod || "",
          insuranceAmount: quote?.insuranceAmount || null,
          insurancePercentage: quote?.insurancePercentage || null,
          insuranceType: quote?.insuranceType || "",
          quoteExpiresOn: quote?.quoteExpiresOn || "",
          contentValue: quote?.contentValue || null,
          accepted_status: quote?.accepted_status || "Initiated",
          hidequotetotal: quote?.hidequotetotal || false,
          isDeletable: quote?.isDeletable ?? false,
          hasFinalInvoice: quote?.hasFinalInvoice ?? false,
          is_reaccept_required: quote?.is_reaccept_required ?? false,
          created_at: quote?.created_at || "",
          updated_at: quote?.updated_at || "",
        };
      });

      /* ================= FINAL STATE ================= */
      console.log("normalized-quotes", quotes);
      state.selectedLeadQuote = {
        quotes,
        isTermConditonPdf: payload.isTermConditonPdf ?? false,
        count: payload.count ?? "1",
      };
    },

    deleteSingleLeadQuoteAction: (state, { payload }) => {},
    setDeleteSingleLeadQuoteAction: (state, { payload }) => {
      state.isSingleLeadQuoteDeletedSuccess = payload;
    },

    setSingleLeadEstimateAction: (state, { payload }) => {
      state.isSingleEstimateResponseSuccess = payload;
    },

    getLeadEstimateLinkAction: (state, { payload }) => {},
    setLeadEstimateDownloaded: (state, { payload }) => {
      state.isFileDownloaded = payload;
    },

    getPreviousQuoteAction: (state, {payload}) => {},
    setPreviousQuoteAction: (state, { payload }) => {
      state.previousQuotes = payload?.quotes;
    },

    getCalculateDistanceAction: (state, { payload }) => {},
    setCalculateDeliveryDistanceAction: (state, { payload }) => {
      state.distanceDeliveryValue = payload?.distance;
    },
    setCalculateCollectionDistanceAction: (state, { payload }) => {
      state.distanceCollectionValue = payload?.distance;
    },
    setCalculateReturnDistanceAction: (state, { payload }) => {
      state.distanceReturnValue = payload?.distance;
    },

    getLeadCustomerAction: (state, { payload }) => {},
    setLeadCustomerResponse: (state, { payload }) => {
      state.leadCustomers = payload;
    },

    getAccountNameListAction: (state) => {},
    setAccountNameListAction: (state, { payload }) => {
      state.accountNamesList = payload;
    },
  },
});

const {
  deleteLeadAddressAction,
  setLeadAddressDeletedAction,
  getAllLeadListAction,
  setAllLeadListAction,
  setLeadListAction,
  setNewLeadLocal,
  resetNewLeadLocal,
  addNewLeadAction,
  setAddNewLeadAction,
  updateLeadAction,
  setUpdateLeadAction,
  getSingleLeadAction,
  setSingleLeadAction,
  getAllReferrerListAction,
  setAllReferrerListAction,
  addLeadLogAction,
  setAddLeadLogAction,
  getAllLeadLogListAction,
  setAllLeadLogListAction,
  addLeadEstimateAction,
  setUpdateLeadEstimateResponse,
  addLeadQuoteAction,
  setUpdateLeadQuoteResponse,
  setLeadEstimateAction,
  setLeadQuoteAction,
  setSingleLeadEstimateAction,
  getLeadEstimateAction,
  getLeadEstimateLinkAction,
  setLeadEstimateDownloaded,
  getCalculateDistanceAction,
  setCalculateDeliveryDistanceAction,
  setCalculateCollectionDistanceAction,
  setCalculateReturnDistanceAction,
  clearLeadDetails,
  addCollectionAddress,
  updateCollectionAddress,
  removeCollectionAddress,
  addDeliveryAddress,
  updateDeliveryAddress,
  removeDeliveryAddress,
  deleteSingleLeadQuoteAction,
  setDeleteSingleLeadQuoteAction,
  getLeadCustomerAction,
  setLeadCustomerResponse,
  getAccountNameListAction,
  setAccountNameListAction,
  getPreviousQuoteAction,
  setPreviousQuoteAction,
} = leadSlice.actions;
export {
  deleteLeadAddressAction,
  setLeadAddressDeletedAction,
  getAllLeadListAction,
  setAllLeadListAction,
  setLeadListAction,
  setNewLeadLocal,
  resetNewLeadLocal,
  addNewLeadAction,
  setAddNewLeadAction,
  updateLeadAction,
  setUpdateLeadAction,
  getSingleLeadAction,
  setSingleLeadAction,
  getAllReferrerListAction,
  setAllReferrerListAction,
  addLeadLogAction,
  setAddLeadLogAction,
  getAllLeadLogListAction,
  setAllLeadLogListAction,
  addLeadEstimateAction,
  setUpdateLeadEstimateResponse,
  addLeadQuoteAction,
  setUpdateLeadQuoteResponse,
  setLeadEstimateAction,
  setLeadQuoteAction,
  setSingleLeadEstimateAction,
  getLeadEstimateAction,
  getLeadEstimateLinkAction,
  setLeadEstimateDownloaded,
  getCalculateDistanceAction,
  setCalculateDeliveryDistanceAction,
  setCalculateCollectionDistanceAction,
  setCalculateReturnDistanceAction,
  clearLeadDetails,
  addCollectionAddress,
  updateCollectionAddress,
  removeCollectionAddress,
  addDeliveryAddress,
  updateDeliveryAddress,
  removeDeliveryAddress,
  deleteSingleLeadQuoteAction,
  setDeleteSingleLeadQuoteAction,
  getLeadCustomerAction,
  setLeadCustomerResponse,
  getAccountNameListAction,
  setAccountNameListAction,
  getPreviousQuoteAction,
  setPreviousQuoteAction,
};

export default leadSlice.reducer;
